<script setup lang="ts">
import { SancareOcticon } from '@sancare/typescript-commons'
import { Dayjs } from 'dayjs'
import { computed, ref } from 'vue'

import router from '@/router'
import { useStore } from '@/store'
import {
  AutomationGroup,
  AutomationGroupStat,
  AutomationModeEnum,
  AutomationTypeEnum
} from '@/store/modules/automation/types'
import { ModeEnum } from '@/store/modules/settings/types'

import AutomationGroupDetails from './AutomationGroupDetails.vue'

const store = useStore()

const props = defineProps<{
  earlyLimit: Dayjs
  lateLimit: Dayjs
  statsGroup: AutomationGroupStat
  fullGroup: AutomationGroup
  ghmCollidingGroupNames: string[]
  demoMode: boolean
}>()

const emit = defineEmits<{
  (e: 'remove-automation-group'): void
}>()

const removeGroupConfirm = ref(false)
const automationConfirm = ref(false)
const criteriaLoaderOnce = ref(false)
const shortHandAutomStayListParam = computed(() => store.getters.shortHandAutomStayListParams)

function joinCollidingGroupNames(collidingGroups) {
  return collidingGroups.map((group) => group.name).join(', ')
}

function toggleAutomation(groupId: number) {
  if (props.fullGroup.automated) {
    store.dispatch('disableAutomationGroup', groupId)
  } else {
    store.dispatch('enableAutomationGroup', groupId).then(() => {
      if (store.state.automation.enableAutomationGroupRequest.ok) {
        store.dispatch('toast/setSuccess', {
          title: 'Automatisation activée',
          message: 'Les séjours correspondants sont validés en arrière-plan. La liste se met à jour au fur et à mesure.',
        })
      }
    })
  }
  automationConfirm.value = false
}

function removeAutomationGroup(groupId: number) {
  emit('remove-automation-group', groupId)
}

function gotoGroupStayList($event: MouseEvent, groupId: number, automationMode: AutomationModeEnum, forceNewWindow: boolean) {
  store.commit('setAutomStayListParam', {
    type: 'automationGroupId',
    value: groupId,
  })
  store.commit('setAutomStayListParam', {
    type: 'automationMode',
    value: automationMode,
  })
  const newRoute = {
    path: '/automation/automation-stay-list',
    query: {
      ...shortHandAutomStayListParam.value,
      groupId,
      automationMode,
      automationType: props.fullGroup.type,
      el: props.earlyLimit.format('DD-MM-YYYY'),
      ll: props.lateLimit.format('DD-MM-YYYY'),
      mode: ModeEnum.AUTOMATION,
      staylistLabel: props.statsGroup.name,
    }
  }

  if (forceNewWindow || $event.ctrlKey || $event.metaKey || $event.shiftKey) {
    window.open(router.resolve(newRoute).href, '_blank')
  } else {
    router.push(newRoute)
  }
}

function showGroupCriteria() {
  if (! criteriaLoaderOnce.value) {
    criteriaLoaderOnce.value = true
    store.dispatch('getAutomationGroup', props.fullGroup.id)
  }
}

function resetConfirmationStateOnClose() {
  automationConfirm.value = false
  removeGroupConfirm.value = false
}
</script>


<template>
  <div class="automation-group-thumbnail hover-parent">
    <div class="automation-group-thumbnail__banner">
      <div class="row automation-group-thumbnail__banner-title g-0">
        <div class="col-8">
          {{ statsGroup.name }}
        </div>
        <div class="ms-auto col-auto">
          <div class="row">
            <v-dropdown
              :triggers="['hover']"
              placement="left"
              class="col-auto popover-container"
              popover-class="saved-search-thumbnail-criteria-popover"
              @show="showGroupCriteria"
            >
              <span>
                <SancareOcticon
                  name="info"
                  :width="18"
                  :height="18"
                />
              </span>
              <template #popper>
                <AutomationGroupDetails
                  v-if="fullGroup.criteriaGroups"
                  :full-automation-group="fullGroup"
                />
                <div
                  v-else
                  class="loader loader-center"
                />
              </template>
            </v-dropdown>
            <v-dropdown
              v-if="ghmCollidingGroupNames.length"
              :triggers="['hover']"
              placement="top"
              class="col-auto"
              popover-class="criteria-popover"
            >
              <span>
                <SancareOcticon
                  name="alert"
                  :width="18"
                  :height="18"
                />
              </span>
              <slot />
              <template #popper>
                <div
                  style="width: 30rem;"
                  class="m-2"
                >
                  Les critères du groupe contiennent des GHM en commun avec le(s) groupe(s) suivant(s):
                  <div style="text-align: center; font-weight: bold;">
                    "{{ joinCollidingGroupNames(ghmCollidingGroupNames) }}"
                  </div>
                  <hr>
                  Rappel:<br>
                  <small>
                    Si un séjour est dans au moins un groupe activé dont les critères sont satisfaits, il est considéré comme automatisable. Et ce, même si un autre groupe dont il fait partie n'a pas ses critères satisfaits.
                  </small>
                </div>
              </template>
            </v-dropdown>
            <v-dropdown
              placement="right"
              class="col-auto popover-container"
              popover-class="saved-search-thumbnail-settings-popover"
              @apply-hide="resetConfirmationStateOnClose"
            >
              <div
                v-tooltip="'Plus d\'infos'"
              >
                <SancareOcticon
                  name="kebab-horizontal"
                  :width="20"
                />
              </div>
              <template #popper>
                <div>
                  <router-link
                    class="btn btn-link"
                    :to="`/automation/edit-automation-group?groupId=${statsGroup.id}&mode=automation`"
                  >
                    Modifier
                  </router-link>
                </div>
                <div
                  v-if="!demoMode"
                >
                  <v-dropdown
                    :shown="automationConfirm"
                    @auto-hide="automationConfirm = false"
                  >
                    <div
                      class="btn btn-link"
                      @click="automationConfirm = true"
                    >
                      {{ props.fullGroup.automated ? 'Ne pas automatiser' : 'Automatiser' }}
                    </div>
                    <template #popper>
                      <div class="p-3">
                        <h4 class="title-light text-center">
                          Êtes-vous sûr(e) ?
                        </h4>
                        <div class="text-center">
                          <button
                            class="btn btn-danger btn-padded"
                            @click="toggleAutomation(statsGroup.id)"
                          >
                            Confirmer
                          </button>
                        </div>
                      </div>
                    </template>
                  </v-dropdown>
                </div>
                <div
                  v-if="demoMode"
                  v-tooltip="'Les groupes ne peuvent pas être automatisés quand le mode démo est activé'"
                  class="home-thumbnail-icon-disabled home-thumbnail-label ms-1 ps-2"
                >
                  {{ props.fullGroup.automated ? 'Ne pas automatiser' : 'Automatiser' }}
                </div>
                <div
                  v-if="!removeGroupConfirm"
                  class="btn btn-link text-danger"
                  @click="removeGroupConfirm = true"
                >
                  Supprimer
                </div>
                <div
                  v-else
                  class="btn btn-link text-danger"
                  @click="removeAutomationGroup(statsGroup.id)"
                >
                  Confirmer
                </div>
                <div>
                  <router-link
                    class="btn btn-link"
                    :to="`/automation/${statsGroup.id}/history?mode=automation`"
                  >
                    Voir l'historique des versions
                  </router-link>
                </div>
              </template>
            </v-dropdown>
          </div>
        </div>
      </div>
      <div class="row align-items-center">
        <div class="col-auto automation-group-thumbnail__banner-count">
          {{ statsGroup.totalCount || 0 }}
        </div>
        <div class="col-auto">
          Séjours correspondants
        </div>
        <div class="col-auto ms-auto px-3">
          <span
            v-tooltip="`Automatisation : ${props.fullGroup.automated ? 'Activée' : 'Désactivée'}`"
            :class="`automation-group-thumbnail__${props.fullGroup.automated ? 'enabled' : 'disabled'}`"
          >
            <SancareOcticon
              name="checklist"
              :width="20"
              :height="20"
            />
          </span>
        </div>
      </div>
    </div>

    <div class="automation-group-thumbnail__body">
      <div class="row g-0 align-items-center automation-group-thumbnail__body-line--low-priority">
        <div class="col-2 text-end automation-group-thumbnail__number">
          {{ props.fullGroup.automated ? statsGroup.automatedCount : statsGroup.automatableCount || 0 }}
        </div>
        <div class="col-8 text-end">
          Séjours {{ props.fullGroup.automated ? 'validés' : 'automatisables' }}
        </div>
        <div class="col-1 text-end">
          <span
            class="hover-child cp"
            @click="gotoGroupStayList($event, statsGroup.id, AutomationModeEnum.AUTOMATABLE, false)"
            @click.middle="gotoGroupStayList($event, statsGroup.id, AutomationModeEnum.AUTOMATABLE, true)"
          >
            <SancareOcticon
              name="arrow-right"
              :width="15"
            />
          </span>
        </div>
      </div>
      <hr v-if="fullGroup.type === AutomationTypeEnum.AI">
      <div
        v-if="fullGroup.type === AutomationTypeEnum.AI"
        class="row g-0 align-items-center automation-group-thumbnail__body-line--average-priority"
      >
        <div class="col-2 text-end automation-group-thumbnail__number">
          {{ statsGroup.waitingCount || 0 }}
        </div>
        <div class="col-8 text-end">
          Séjours en attente d'information
        </div>
        <div class="col-1 text-end">
          <span
            class="hover-child cp"
            @click="gotoGroupStayList($event, statsGroup.id, AutomationModeEnum.WAITING, false)"
            @click.middle="gotoGroupStayList($event, statsGroup.id, AutomationModeEnum.WAITING, true)"
          >
            <SancareOcticon
              name="arrow-right"
              :width="15"
            />
          </span>
        </div>
      </div>
      <hr v-if="fullGroup.type === AutomationTypeEnum.AI">
      <div
        v-if="fullGroup.type === AutomationTypeEnum.AI"
        class="row g-0 align-items-center automation-group-thumbnail__body-line--high-priority"
      >
        <div class="col-2 text-end automation-group-thumbnail__number">
          {{ statsGroup.expiredCount || 0 }}
        </div>
        <div class="col-8 text-end">
          Séjours à faire manuellement
        </div>
        <div class="col-1 text-end">
          <span
            class="hover-child cp"
            @click="gotoGroupStayList($event, statsGroup.id, AutomationModeEnum.EXPIRED, false)"
            @click.middle="gotoGroupStayList($event, statsGroup.id, AutomationModeEnum.EXPIRED, true)"
          >
            <SancareOcticon
              name="arrow-right"
              :width="15"
            />
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="false"
      class="automation-group-thumbnail__footer text-center"
    >
      <div>Contrôle Qualité</div>
      <small>(233 Séjours)</small>
    </div>
  </div>
</template>
